import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Bell } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { ScrollArea } from "./ui/scroll-area";
import { timeAgo } from "../lib/format";

export function NotificationBell({ dark = false }) {
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  const navigate = useNavigate();

  const { data: count = { count: 0 } } = useQuery({
    queryKey: ["notif-count"],
    queryFn: async () => (await api.get("/notifications/unread-count")).data,
    refetchInterval: 30000,
  });
  const { data: notifs = [] } = useQuery({
    queryKey: ["notifs"],
    queryFn: async () => (await api.get("/notifications")).data,
    enabled: open,
  });

  const markRead = async (n) => {
    await api.post(`/notifications/${n.id}/read`);
    qc.invalidateQueries({ queryKey: ["notif-count"] });
    qc.invalidateQueries({ queryKey: ["notifs"] });
    setOpen(false);
    if (n.link) navigate(n.link);
  };
  const markAll = async () => {
    await api.post("/notifications/read-all");
    qc.invalidateQueries({ queryKey: ["notif-count"] });
    qc.invalidateQueries({ queryKey: ["notifs"] });
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid="notification-bell" className={`relative p-2 rounded-md transition-colors ${dark ? "hover:bg-arq-card/10 text-arq-bg" : "hover:bg-arq-surface text-arq-ink"}`}>
          <Bell className="h-5 w-5" strokeWidth={1.6} />
          {count.count > 0 && (
            <span data-testid="notification-count" className="absolute top-1 right-1 h-4 min-w-4 px-1 flex items-center justify-center text-[9px] font-bold text-white bg-arq-accent rounded-full">
              {count.count}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notification-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <span className="font-display text-base">Notifications</span>
          <button onClick={markAll} className="text-xs text-arq-accent hover:underline" data-testid="mark-all-read">Mark all read</button>
        </div>
        <ScrollArea className="h-80">
          {notifs.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-10">You're all caught up.</p>
          ) : notifs.map((n) => (
            <button key={n.id} onClick={() => markRead(n)} data-testid={`notification-${n.id}`}
              className={`w-full text-left px-4 py-3 border-b last:border-0 hover:bg-arq-surface/60 transition-colors ${!n.read ? "bg-arq-surface/40" : ""}`}>
              <div className="flex items-start gap-2">
                {!n.read && <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-arq-accent shrink-0" />}
                <div className={n.read ? "pl-3.5" : ""}>
                  <p className="text-sm font-medium text-arq-ink leading-snug">{n.title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5 leading-snug">{n.message}</p>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">{timeAgo(n.created_at)}</p>
                </div>
              </div>
            </button>
          ))}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
